/**
 * Simple WhatsApp Setup
 *
 * Setup semplificato per utenti finali:
 * - Le credenziali WAHA sono già configurate nel .env
 * - L'utente vede solo: "Connetti WhatsApp" → QR code → Fatto
 */

import React, { useState, useEffect } from 'react';
import { Loader, Check, AlertCircle, RefreshCw, MessageCircle } from 'lucide-react';
import { useApp } from '../AppProvider';
import { supabase } from '../../lib/supabase';
import {
  createPreliminaryConfig,
  updateWahaConfigWithSession,
  type SessionStatus
} from '../../lib/utils/whatsapp-setup';
import {
  deleteWahaSession,
  createWahaSession,
  startWahaSession,
  getWahaQRCode,
  getWahaSessionStatus,
  getWahaSessionDetails,
} from '../../lib/services/wahaProxyService';

interface SimpleWhatsAppSetupProps {
  organizationId: string;
  existingConfigId?: string | null;
  onComplete: () => void;
  skipCleanup?: boolean;
  forceNewConfig?: boolean;
}

type Step = 'connect' | 'qr' | 'success' | 'redirecting';

export function SimpleWhatsAppSetup({ organizationId, existingConfigId, onComplete, skipCleanup, forceNewConfig }: SimpleWhatsAppSetupProps) {
  const { showToast } = useApp();

  const [currentStep, setCurrentStep] = useState<Step>('connect');
  const [configId, setConfigId] = useState('');
  const [sessionName, setSessionName] = useState('');
  const [qrCode, setQrCode] = useState('');
  const [sessionStatus, setSessionStatus] = useState<SessionStatus>('STARTING');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [loading, setLoading] = useState(false);
  const [pollingInterval, setPollingInterval] = useState<NodeJS.Timeout | null>(null);
  const [userId, setUserId] = useState<string | null>(null);

  // WAHA URL è ancora visibile (non è un segreto), le credenziali passano dal proxy server-side
  const wahaUrl = import.meta.env.VITE_WAHA_URL;

  // Recupera user_id al mount
  useEffect(() => {
    const fetchUserId = async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (user) {
        setUserId(user.id);
        console.log('[SimpleSetup] 👤 User ID:', user.id);
      }
    };
    fetchUserId();
  }, []);

  // Cleanup polling
  useEffect(() => {
    return () => {
      if (pollingInterval) {
        clearInterval(pollingInterval);
      }
    };
  }, [pollingInterval]);

  // Forza cambio step quando arriva il QR
  useEffect(() => {
    if (qrCode && sessionName && currentStep === 'connect') {
      setCurrentStep('qr');
    }
  }, [qrCode, sessionName, currentStep]);

  /**
   * Connetti WhatsApp
   */
  const handleConnect = async () => {
    setLoading(true);

    try {
      let configIdToUse: string;

      // 1. PULIZIA: Rimuovi vecchie config e sessioni WAHA (skip se multi-sessione)
      if (!existingConfigId && userId && !skipCleanup) {
        console.log('[SimpleSetup] 🧹 Pulizia vecchie configurazioni...');

        // Cerca vecchie config per questo utente
        const { data: oldConfigs } = await supabase
          .from('api_configurations')
          .select('id, configuration')
          .eq('service_name', 'waha')
          .eq('organization_id', organizationId)
          .eq('user_id', userId);

        if (oldConfigs && oldConfigs.length > 0) {
          console.log('[SimpleSetup] 🗑️ Trovate', oldConfigs.length, 'vecchie config da rimuovere');

          // Per ogni vecchia config, elimina la sessione WAHA (via proxy)
          for (const oldConfig of oldConfigs) {
            const oldSession = oldConfig.configuration?.session;
            if (oldSession) {
              console.log('[SimpleSetup] 🗑️ Eliminazione sessione WAHA:', oldSession);
              try {
                await deleteWahaSession(oldSession);
                console.log('[SimpleSetup] ✅ Sessione WAHA eliminata');
              } catch (err) {
                console.warn('[SimpleSetup] ⚠️ Errore eliminazione sessione WAHA:', err);
              }
            }

            // Elimina la config dal DB
            await supabase
              .from('api_configurations')
              .delete()
              .eq('id', oldConfig.id);
          }

          // Elimina anche da whatsapp_user_access
          await supabase
            .from('whatsapp_user_access')
            .delete()
            .eq('user_id', userId)
            .eq('organization_id', organizationId);

          console.log('[SimpleSetup] ✅ Pulizia completata');
        }

        // Pulisci cache
        sessionStorage.clear();
        console.log('[SimpleSetup] ✅ Cache pulita');
      }

      // 2. Usa config esistente o crea nuova
      if (existingConfigId) {
        configIdToUse = existingConfigId;
        setConfigId(existingConfigId);
        showToast?.('Ripresa configurazione...', 'info');
      } else {
        showToast?.('Creazione configurazione...', 'info');
        // createPreliminaryConfig salva la config nel DB (non chiama WAHA direttamente)
        const preliminaryConfigId = await createPreliminaryConfig(
          wahaUrl,
          '', // apiKey non più necessaria qui (salvata server-side)
          organizationId,
          userId || undefined,
          forceNewConfig // forceNew: solo quando esplicitamente richiesto (Settings → Aggiungi sessione)
        );
        configIdToUse = preliminaryConfigId;
        setConfigId(preliminaryConfigId);
      }

      // 3. Crea sessione con webhook (via proxy — credenziali server-side)
      showToast?.('Creazione sessione WhatsApp...', 'info');

      const newSessionName = `org_${organizationId.slice(0, 8)}_${Date.now()}`;
      const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;

      await createWahaSession(newSessionName, configIdToUse, supabaseAnonKey);
      console.log('[SimpleSetup] ✅ Sessione creata');

      // Avvia sessione
      await startWahaSession(newSessionName);
      console.log('[SimpleSetup] ✅ Sessione avviata');

      // Attendi che la sessione si avvii
      await new Promise(resolve => setTimeout(resolve, 2000));

      // Ottieni QR code
      const qr = await getWahaQRCode(newSessionName);
      console.log('[SimpleSetup] ✅ QR code ottenuto');

      // 4. Aggiorna stati (useEffect cambierà lo step automaticamente)
      setSessionName(newSessionName);
      setQrCode(qr);
      setSessionStatus('SCAN_QR_CODE');
      setLoading(false);

      // 5. Avvia polling (passa configId per aggiornamento DB)
      startPolling(newSessionName, configIdToUse);

      showToast?.('Scansiona il QR code con WhatsApp!', 'success');

    } catch (error) {
      console.error('Errore durante la connessione:', error);
      showToast?.('Errore durante la connessione. Riprova.', 'error');
      setLoading(false);
    }
  };

  /**
   * Rigenera QR code
   */
  const handleRefreshQR = async () => {
    setLoading(true);
    try {
      const qr = await getWahaQRCode(sessionName);
      setQrCode(qr);
      showToast?.('QR code rigenerato!', 'success');
    } catch (error) {
      console.error('Errore rigenerazione QR:', error);
      showToast?.('Errore durante la rigenerazione del QR code', 'error');
    } finally {
      setLoading(false);
    }
  };

  /**
   * Avvia polling status
   */
  const startPolling = (session: string, configIdToUse: string) => {
    if (pollingInterval) {
      clearInterval(pollingInterval);
    }

    const interval = setInterval(async () => {
      try {
        const status = await getWahaSessionStatus(session) as SessionStatus;
        setSessionStatus(status);

        if (status === 'WORKING') {
          clearInterval(interval);
          setPollingInterval(null);

          try {
            // 1. Ottieni dettagli sessione
            console.log('[SimpleSetup] 📞 Recupero dettagli sessione...');
            const details = await getWahaSessionDetails(session);
            setPhoneNumber(details.phoneNumber || '');

            // 2. Salva configurazione nel DB
            console.log('[SimpleSetup] 💾 Salvataggio configurazione nel DB...');
            console.log('[SimpleSetup] 💾 Config ID:', configIdToUse);
            console.log('[SimpleSetup] 💾 Session:', session);
            console.log('[SimpleSetup] 💾 Phone:', details.phoneNumber);

            // Check: questo numero è già collegato in un'altra config dell'org?
            if (details.phoneNumber) {
              const { data: existingWithSamePhone } = await supabase
                .from('api_configurations')
                .select('id, configuration')
                .eq('service_name', 'waha')
                .eq('organization_id', organizationId)
                .eq('is_active', true)
                .neq('id', configIdToUse);

              const duplicate = existingWithSamePhone?.find(
                c => c.configuration?.phone_number === details.phoneNumber
              );

              if (duplicate) {
                console.warn('[SimpleSetup] ⚠️ Numero già collegato in altra config:', duplicate.id);
                // Elimina la sessione WAHA appena creata (è un duplicato)
                try { await deleteWahaSession(session); } catch {}
                // Elimina la config preliminare se era nuova (non existingConfigId)
                if (!existingConfigId) {
                  await supabase.from('api_configurations').delete().eq('id', configIdToUse);
                }
                clearInterval(interval);
                setPollingInterval(null);
                showToast?.(`Questo numero (${details.phoneNumber}) è già collegato in un\'altra sessione. Usa una sessione diversa o scollega prima quella esistente.`, 'error');
                setCurrentStep('connect');
                return;
              }
            }

            await updateWahaConfigWithSession(configIdToUse, session, details.phoneNumber || '');

            console.log('[SimpleSetup] ✅ Configurazione salvata con successo!');

            // Se abbiamo uno userId, crea anche l'entry in whatsapp_user_access
            if (userId) {
              console.log('[SimpleSetup] 👤 Creazione accesso utente in whatsapp_user_access...');
              const { error: accessError } = await supabase
                .from('whatsapp_user_access')
                .upsert({
                  user_id: userId,
                  organization_id: organizationId,
                  configuration_id: configIdToUse
                }, {
                  onConflict: 'user_id,configuration_id,organization_id'
                });

              if (accessError) {
                console.error('[SimpleSetup] ⚠️ Errore creazione accesso utente:', accessError);
              } else {
                console.log('[SimpleSetup] ✅ Accesso utente creato con successo!');
              }
            }

            // 3. Imposta flag per evitare sync iniziale (già configurato)
            console.log('[SimpleSetup] ✅ Impostazione flag whatsapp_initial_sync_done...');
            localStorage.setItem('whatsapp_initial_sync_done', 'true');

            // 4. Mostra schermata di caricamento
            setCurrentStep('redirecting');
            showToast?.('WhatsApp connesso! 🎉', 'success');

            // 5. Invalida TUTTE le cache
            console.log('[SimpleSetup] 🧹 Pulizia cache completa...');
            sessionStorage.clear(); // Pulisci tutta la sessionStorage

            // ✅ NUOVO: Verifica che sessione sia stabile prima di completare
            setTimeout(async () => {
              try {
                console.log('[SimpleSetup] 🔍 Verifica finale sessione...');

                // Doppio check: verifica che la sessione sia ancora WORKING
                const finalStatus = await getWahaSessionStatus(session);

                if (finalStatus === 'WORKING') {
                  console.log('[SimpleSetup] ✅ Sessione confermata WORKING. Completamento setup...');

                  // Invece di reload, chiama il callback onComplete
                  // In questo modo il parent (WhatsApp2Page) decide cosa fare
                  onComplete();
                } else {
                  console.error('[SimpleSetup] ❌ Sessione non stabile:', finalStatus);
                  showToast?.('Sessione instabile. Riprova.', 'error');
                  setCurrentStep('connect');
                }
              } catch (error) {
                console.error('[SimpleSetup] ❌ Errore verifica finale:', error);
                showToast?.('Errore durante la verifica. Riprova.', 'error');
                setCurrentStep('connect');
              }
            }, 3000); // ← Aumentato a 3s per dare tempo al DB di salvare

          } catch (error) {
            console.error('[SimpleSetup] ❌ Errore salvataggio configurazione:', error);
            showToast?.('Errore durante il salvataggio. Riprova.', 'error');
            setCurrentStep('connect');
          }
        }

        if (status === 'FAILED') {
          clearInterval(interval);
          setPollingInterval(null);
          showToast?.('Connessione fallita. Riprova.', 'error');
          setCurrentStep('connect');
        }
      } catch (error) {
        console.error('Errore polling:', error);
      }
    }, 3000);

    setPollingInterval(interval);
  };

  /**
   * Render: Connect (Notion-style)
   */
  if (currentStep === 'connect') {
    return (
      <div className="h-full overflow-y-auto bg-[#FBFBFA] dark:bg-gray-950">
        <div className="max-w-2xl mx-auto px-6 py-12">
          {/* Header */}
          <div className="text-center mb-10">
            <div className="w-20 h-20 rounded-2xl bg-gradient-to-br from-[#25D366] to-[#128C7E] flex items-center justify-center mx-auto mb-6 shadow-lg">
              <MessageCircle className="w-10 h-10 text-white" />
            </div>
            <h1 className="text-2xl sm:text-3xl font-bold text-[#37352F] dark:text-white mb-3">
              Connetti WhatsApp
            </h1>
            <p className="text-base text-[#787774] max-w-md mx-auto">
              Collega il tuo account WhatsApp Business per iniziare a gestire le conversazioni con i tuoi clienti.
            </p>
          </div>

          {/* Steps preview */}
          <div className="grid gap-4 mb-10">
            <div className="flex items-start gap-4 p-4 rounded-xl bg-white dark:bg-gray-900 border border-[rgba(55,53,47,0.09)] dark:border-white/10">
              <div className="w-10 h-10 rounded-full bg-[#25D366]/10 flex items-center justify-center flex-shrink-0 text-[#25D366] font-bold">
                1
              </div>
              <div>
                <h3 className="font-medium text-[#37352F] dark:text-white mb-1">Premi "Connetti WhatsApp"</h3>
                <p className="text-sm text-[#787774]">Clicca il pulsante qui sotto per generare un codice QR univoco.</p>
              </div>
            </div>

            <div className="flex items-start gap-4 p-4 rounded-xl bg-white dark:bg-gray-900 border border-[rgba(55,53,47,0.09)] dark:border-white/10">
              <div className="w-10 h-10 rounded-full bg-[#25D366]/10 flex items-center justify-center flex-shrink-0 text-[#25D366] font-bold">
                2
              </div>
              <div>
                <h3 className="font-medium text-[#37352F] dark:text-white mb-1">Apri WhatsApp sul telefono</h3>
                <p className="text-sm text-[#787774]">Vai su Menu → Dispositivi collegati → Collega un dispositivo.</p>
              </div>
            </div>

            <div className="flex items-start gap-4 p-4 rounded-xl bg-white dark:bg-gray-900 border border-[rgba(55,53,47,0.09)] dark:border-white/10">
              <div className="w-10 h-10 rounded-full bg-[#25D366]/10 flex items-center justify-center flex-shrink-0 text-[#25D366] font-bold">
                3
              </div>
              <div>
                <h3 className="font-medium text-[#37352F] dark:text-white mb-1">Scansiona il QR code</h3>
                <p className="text-sm text-[#787774]">Inquadra il codice QR con la fotocamera e sei connesso!</p>
              </div>
            </div>
          </div>

          {/* CTA */}
          <div className="text-center">
            <button
              onClick={handleConnect}
              disabled={loading || !userId}
              className="inline-flex items-center gap-3 px-8 py-4 bg-gradient-to-r from-[#25D366] to-[#128C7E] text-white font-medium text-lg rounded-xl hover:shadow-lg hover:scale-[1.02] transition-all disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100"
            >
              {loading ? (
                <>
                  <Loader className="w-6 h-6 animate-spin" />
                  Connessione in corso...
                </>
              ) : (
                <>
                  <MessageCircle className="w-6 h-6" />
                  Connetti WhatsApp
                </>
              )}
            </button>
            <p className="text-xs text-[#787774] mt-4">
              La configurazione richiede solo pochi minuti
            </p>
          </div>
        </div>
      </div>
    );
  }

  /**
   * Render: QR Code (Notion-style)
   */
  if (currentStep === 'qr') {
    return (
      <div className="h-full overflow-y-auto bg-[#FBFBFA] dark:bg-gray-950">
        <div className="max-w-lg mx-auto px-6 py-8">
          {/* Header compatto */}
          <div className="text-center mb-5">
            <h1 className="text-xl font-bold text-[#37352F] dark:text-white mb-1">
              Scansiona il QR Code
            </h1>
            <p className="text-sm text-[#787774]">
              Inquadra il codice con WhatsApp per collegare il tuo account
            </p>
          </div>

          {/* QR Code - più piccolo */}
          {qrCode ? (
            <div className="flex justify-center mb-5">
              <div className="p-4 bg-white dark:bg-gray-900 rounded-xl shadow-lg border border-[rgba(55,53,47,0.09)] dark:border-white/10">
                <img
                  src={qrCode.startsWith('data:') ? qrCode : `data:image/png;base64,${qrCode}`}
                  alt="QR Code WhatsApp"
                  className="w-48 h-48"
                  onError={(e) => {
                    console.error('Errore caricamento QR code');
                    e.currentTarget.style.display = 'none';
                  }}
                />
              </div>
            </div>
          ) : (
            <div className="flex justify-center mb-5">
              <div className="p-4 bg-white dark:bg-gray-900 rounded-xl shadow-lg border border-[rgba(55,53,47,0.09)] dark:border-white/10">
                <div className="w-48 h-48 flex items-center justify-center">
                  <Loader className="h-10 w-10 text-[#787774] animate-spin" />
                </div>
              </div>
            </div>
          )}

          {/* Status + Rigenera QR inline */}
          <div className="flex items-center justify-center gap-4 mb-5">
            <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-blue-50 dark:bg-blue-900/20 border border-blue-100 dark:border-blue-800">
              <Loader className="w-4 h-4 text-[#2383E2] animate-spin" />
              <span className="text-sm text-[#2383E2] font-medium">In attesa di scansione...</span>
            </div>
            <button
              onClick={handleRefreshQR}
              disabled={loading}
              className="inline-flex items-center gap-1.5 px-3 py-2 text-xs text-[#37352F] dark:text-white border border-[rgba(55,53,47,0.16)] dark:border-white/20 rounded-lg hover:bg-[rgba(55,53,47,0.04)] dark:hover:bg-white/5 transition-colors disabled:opacity-50"
            >
              <RefreshCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} />
              Rigenera QR
            </button>
          </div>

          {/* Steps compatti */}
          <div className="flex items-center gap-2 justify-center mb-4 text-xs text-[#787774]">
            <span className="flex items-center gap-1"><span className="w-5 h-5 rounded-full bg-[#25D366]/10 text-[#25D366] font-bold inline-flex items-center justify-center text-[10px]">1</span> Apri WhatsApp</span>
            <span>→</span>
            <span className="flex items-center gap-1"><span className="w-5 h-5 rounded-full bg-[#25D366]/10 text-[#25D366] font-bold inline-flex items-center justify-center text-[10px]">2</span> Dispositivi collegati</span>
            <span>→</span>
            <span className="flex items-center gap-1"><span className="w-5 h-5 rounded-full bg-[#25D366]/10 text-[#25D366] font-bold inline-flex items-center justify-center text-[10px]">3</span> Scansiona QR</span>
          </div>

          {/* Avvisi */}
          <div className="space-y-2">
            <div className="flex items-start gap-2 p-3 rounded-lg bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800">
              <AlertCircle className="w-4 h-4 text-amber-600 dark:text-amber-400 flex-shrink-0 mt-0.5" />
              <p className="text-xs text-amber-800 dark:text-amber-300">
                Se su WhatsApp appare <strong>"Al momento non puoi collegare dispositivi"</strong>, clicca su <strong>Rigenera QR</strong> e riprova.
              </p>
            </div>
            <div className="flex items-start gap-2 p-3 rounded-lg bg-blue-50 dark:bg-blue-900/20 border border-blue-100 dark:border-blue-800">
              <AlertCircle className="w-4 h-4 text-blue-500 dark:text-blue-400 flex-shrink-0 mt-0.5" />
              <div className="text-xs text-blue-800 dark:text-blue-300">
                <p><strong>Dopo la scansione</strong> il caricamento delle chat potrebbe richiedere qualche minuto.</p>
                <p className="mt-1">Se le chat non si caricano, esci da questa schermata e rientra.</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  /**
   * Render: Redirecting (Notion-style)
   */
  if (currentStep === 'redirecting') {
    return (
      <div className="h-full flex items-center justify-center bg-[#FBFBFA] dark:bg-gray-950">
        <div className="text-center max-w-md px-6">
          <div className="w-20 h-20 rounded-2xl bg-gradient-to-br from-[#25D366] to-[#128C7E] flex items-center justify-center mx-auto mb-6 shadow-lg relative">
            <Check className="w-10 h-10 text-white" />
            <div className="absolute inset-0 rounded-2xl border-4 border-white/30 border-t-transparent animate-spin"></div>
          </div>

          <h2 className="text-2xl font-bold text-[#37352F] dark:text-white mb-3">
            Connessione riuscita!
          </h2>

          <div className="flex items-center justify-center gap-3 mb-6">
            <Loader className="w-5 h-5 text-[#25D366] animate-spin" />
            <p className="text-[#787774]">
              Caricamento conversazioni...
            </p>
          </div>

          <div className="p-4 rounded-xl bg-green-50 dark:bg-green-900/20 border border-green-100 dark:border-green-800">
            <p className="text-sm text-green-700 dark:text-green-300">
              Preparazione della dashboard WhatsApp in corso
            </p>
          </div>
        </div>
      </div>
    );
  }

  /**
   * Render: Success (Notion-style)
   */
  if (currentStep === 'success') {
    return (
      <div className="h-full flex items-center justify-center bg-[#FBFBFA] dark:bg-gray-950">
        <div className="text-center max-w-md px-6">
          <div className="w-20 h-20 rounded-2xl bg-gradient-to-br from-[#25D366] to-[#128C7E] flex items-center justify-center mx-auto mb-6 shadow-lg">
            <Check className="w-10 h-10 text-white" />
          </div>

          <h2 className="text-2xl font-bold text-[#37352F] dark:text-white mb-3">
            WhatsApp Connesso!
          </h2>

          <p className="text-[#787774] mb-6">
            Il tuo account WhatsApp è stato collegato con successo
          </p>

          <div className="bg-white dark:bg-gray-900 rounded-xl p-5 mb-8 border border-[rgba(55,53,47,0.09)] dark:border-white/10">
            <div className="space-y-3">
              <div className="flex justify-between items-center">
                <span className="text-sm text-[#787774]">Sessione:</span>
                <span className="text-sm font-mono text-[#37352F] dark:text-white">{sessionName}</span>
              </div>

              {phoneNumber && (
                <div className="flex justify-between items-center">
                  <span className="text-sm text-[#787774]">Telefono:</span>
                  <span className="text-sm font-medium text-[#37352F] dark:text-white">{phoneNumber}</span>
                </div>
              )}

              <div className="flex justify-between items-center">
                <span className="text-sm text-[#787774]">Status:</span>
                <span className="flex items-center gap-2 text-sm font-medium text-[#25D366]">
                  <span className="w-2 h-2 bg-[#25D366] rounded-full"></span>
                  Connesso
                </span>
              </div>
            </div>
          </div>

          <button
            onClick={onComplete}
            className="inline-flex items-center gap-3 px-8 py-4 bg-gradient-to-r from-[#25D366] to-[#128C7E] text-white font-medium text-lg rounded-xl hover:shadow-lg hover:scale-[1.02] transition-all"
          >
            Vai a WhatsApp
            <MessageCircle className="w-6 h-6" />
          </button>
        </div>
      </div>
    );
  }

  // Fallback
  return (
    <div className="max-w-2xl mx-auto">
      <div className="bg-white rounded-lg shadow-lg p-8">
        <div className="text-center">
          <h2 className="text-xl text-red-600 mb-4">Errore: Step non valido</h2>
          <p className="text-gray-600 mb-4">currentStep: {currentStep}</p>
          <button
            onClick={() => setCurrentStep('connect')}
            className="px-4 py-2 bg-[#0f6cbd] text-white rounded hover:bg-[#0a5ba8]"
          >
            Riprova
          </button>
        </div>
      </div>
    </div>
  );
}
